/**
 * State 2 — Free user, Skills Assessment DONE, zero courses started.
 * Goal: get the user to start and complete their first free course.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardUser } from './types';
import { HeroBanner } from './shared/HeroBanner';
import { LockedPlan } from './shared/LockedPlan';
import { UpgradeCard } from './shared/UpgradeCard';
import { CommunitySignals, CommunitySignal } from './shared/CommunitySignals';
import { TodayGoal, Goal } from './shared/TodayGoal';
import { ROUTES } from '../../utils/constants';
import { courseService } from '../../services/firebase/courses';
import { Course } from '../../types/course';

interface Props {
  user: DashboardUser;
}

const COMMUNITY_SIGNALS: CommunitySignal[] = [
  {
    name: 'Eric R.',
    initials: 'ER',
    avatarColor: '#5b6cff',
    action: 'shared a win in #small-business 🎉',
    timeAgo: '4 min ago',
    isNew: true,
  },
  {
    name: 'Paul W.',
    initials: 'PW',
    avatarColor: '#ff6b6b',
    action: 'asked a question in #support',
    timeAgo: '12 min ago',
  },
  {
    name: 'Jenny K.',
    initials: 'JK',
    avatarColor: '#f59e0b',
    action: 'posted in #marketing-ai',
    timeAgo: '31 min ago',
  },
];

export const DashState2AssessmentDone: React.FC<Props> = ({ user }) => {
  const results = user.assessmentResults;

  // Locked courses still come from assessment matched courses (non-free)
  const lockedCourses = results?.matchedCourses.filter(c => !c.isFree) ?? [];

  // Featured free course — resolved from real course data
  const [featuredCourse, setFeaturedCourse] = useState<Course | null>(null);

  useEffect(() => {
    courseService
      .getCourses({ status: 'published' })
      .then(({ courses }) => {
        const freeCourses = courses.filter(c => c.isFree);

        // Priority 1: first assessment-matched course that is free
        const firstFreeMatchId = results?.matchedCourses.find(c => c.isFree)?.id;
        let featured: Course | undefined;
        if (firstFreeMatchId) {
          featured = freeCourses.find(c => c.id === firstFreeMatchId);
        }
        // Priority 2: first free course in default sort order
        if (!featured) {
          featured = freeCourses[0];
        }
        setFeaturedCourse(featured ?? null);
      })
      .catch(() => setFeaturedCourse(null));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startedFreeCourse = user.enrolledCourses.some(c => c.isFree && c.progressPct > 0);

  const goals: Goal[] = [
    { label: 'Start your first free course', done: startedFreeCourse },
    // TODO: wire to community posts when data is available (no existing query for user posts)
    { label: 'Join the community', done: false },
    // Upgrade to Premium — always unchecked in State 2 by definition (free user)
    { label: 'Upgrade to Premium', done: false },
  ];

  const duration = featuredCourse?.estimatedDuration;
  const difficulty =
    featuredCourse?.difficulty
      ? featuredCourse.difficulty.charAt(0).toUpperCase() + featuredCourse.difficulty.slice(1)
      : 'Beginner';

  return (
    <div style={{ background: '#f7f8ff', minHeight: '100%' }}>
      <main className="container-custom py-10">
        {/* Hero */}
        <HeroBanner
          headline={`Your plan is ready, ${user.name}. Now let's get started.`}
          sub={`We've matched ${results?.matchedCourses?.length ?? 4} courses to your goals.${duration ? ` Your first free course takes just ${duration} minutes.` : ''}`}
          chip="✅ Assessment Complete"
          chipVariant="teal"
          animDelay={0}
        />

        {/* Mobile: Today's Goals immediately below hero */}
        <div className="block lg:hidden mb-6">
          <TodayGoal goals={goals} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* ── LEFT COLUMN (2/3) ────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-6">
            {/* Featured free course */}
            {featuredCourse && (
              <div
                id="featured-course"
                className="db-fade-up rounded-2xl overflow-hidden"
                style={{
                  border: '1.5px solid #e8e8ee',
                  boxShadow: '0 4px 24px rgba(91,108,255,0.10)',
                  animationDelay: '100ms',
                }}
              >
                {/* Teal gradient header */}
                <div
                  className="p-6 flex items-center gap-4"
                  style={{ background: 'linear-gradient(135deg, #00b89a, #00c2a8)' }}
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{ background: 'rgba(255,255,255,0.18)' }}
                  >
                    {featuredCourse.thumbnail ? (
                      <img
                        src={featuredCourse.thumbnail}
                        alt={featuredCourse.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-3xl">📚</span>
                    )}
                  </div>
                  <div>
                    <h2 className="font-montserrat font-extrabold text-xl text-white mb-1">
                      {featuredCourse.title}
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {[
                        'Free',
                        `${featuredCourse.estimatedDuration} min`,
                        difficulty,
                        'Your #1 recommended course',
                      ].map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-0.5 rounded-full font-montserrat font-bold text-xs"
                          style={{
                            background: 'rgba(255,255,255,0.22)',
                            color: '#fff',
                            fontSize: '11px',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6 bg-white">
                  <p
                    className="font-open-sans text-sm text-neutral-600 leading-relaxed mb-6"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    } as React.CSSProperties}
                  >
                    {featuredCourse.shortDescription || featuredCourse.description}
                  </p>
                  <Link
                    to={`${ROUTES.courses}/${featuredCourse.id}`}
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-montserrat font-bold text-base text-white transition-all duration-200 hover:-translate-y-0.5"
                    style={{
                      background: 'linear-gradient(135deg, #00b89a, #00c2a8)',
                      boxShadow: '0 4px 18px rgba(0,194,168,0.30)',
                    }}
                  >
                    Begin Course — It's Free →
                  </Link>
                </div>
              </div>
            )}

            {/* ── SECONDARY UPGRADE CARD ───────────────────────────── */}
            <div style={{ animationDelay: '175ms' }}>
              <UpgradeCard />
            </div>

            {/* Locked learning plan */}
            {lockedCourses.length > 0 && (
              <div
                className="db-fade-up"
                style={{ animationDelay: '200ms' }}
              >
                <h3 className="font-montserrat font-bold text-sm text-neutral-800 mb-3">
                  Your learning plan
                </h3>
                <LockedPlan courses={lockedCourses} showState2Buttons={true} />
              </div>
            )}

            {/* Social stat */}
            <div
              className="db-fade-up text-center py-4"
              style={{ animationDelay: '300ms' }}
            >
              <span
                className="font-montserrat font-extrabold text-3xl"
                style={{ color: '#5b6cff' }}
              >
                1,000+
              </span>
              <span className="font-open-sans text-sm text-neutral-600 ml-2">
                people completed their first course this month
              </span>
            </div>
          </div>

          {/* ── RIGHT SIDEBAR (1/3) ──────────────────────────────────── */}
          <div className="space-y-5">
            {/* Today's Goals — first card in sidebar, hidden on mobile (shown above hero on mobile) */}
            <div className="db-fade-up hidden lg:block" style={{ animationDelay: '100ms' }}>
              <TodayGoal goals={goals} />
            </div>

            {/* Assessment summary */}
            {results && (
              <div
                className="db-fade-up rounded-2xl p-5 bg-white"
                style={{ border: '1.5px solid #e8e8ee', animationDelay: '100ms' }}
              >
                <p className="font-montserrat font-bold text-sm text-neutral-800 mb-4">
                  📊 Your Assessment Results
                </p>
                <div className="space-y-2.5">
                  {[
                    { label: 'Role', value: results.role },
                    { label: 'AI Experience', value: results.experience },
                    { label: 'Top Goal', value: results.topGoal },
                    { label: 'Weekly Time', value: results.weeklyTime },
                    { label: 'Courses Matched', value: `${results.matchedCourses.length} courses` },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-start justify-between gap-3">
                      <span className="font-open-sans text-xs text-neutral-500 flex-shrink-0">
                        {label}
                      </span>
                      <span className="font-open-sans text-xs font-semibold text-neutral-800 text-right">
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Community signals */}
            <div className="db-fade-up" style={{ animationDelay: '200ms' }}>
              <CommunitySignals signals={COMMUNITY_SIGNALS} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
